import React, { useCallback, useRef } from "react";
import PropTypes from "prop-types";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { useTranslation } from "react-i18next";
import { CONSTANTS as C } from "config";
import { errorNotification } from "utils/Utils";
import { AppFieldWithLabelAndError } from "components/ui/AppTypography";

import {
  GoogleMap as MapGoogle,
  useJsApiLoader,
  Marker,
} from "@react-google-maps/api";
import { libraries } from "features/shipping_methods/helpers";

const TARGET_MARKER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20" fill="white" stroke="#E5E7EB" stroke-width="1"/><circle cx="22" cy="22" r="14" fill="none" stroke="#DC2626" stroke-width="2.5"/><circle cx="22" cy="22" r="6" fill="none" stroke="#DC2626" stroke-width="2"/><circle cx="22" cy="22" r="3" fill="#DC2626"/></svg>`;
const TARGET_MARKER_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  TARGET_MARKER_SVG
)}`;
const MyMap = (props) => {
  const mapref = useRef(null);
  const { t } = useTranslation();

  const handleChange = (address) => props.handleValues("mapAddress", address);

  const handleSelect = (address) => {
    props.handleValues("mapAddress", address);
    geocodeByAddress(address)
      .then((results) => getLatLng(results[0]))
      .then((latLng) => {
        props.handleValues("latlng", latLng);
        updateZoom(21);
      })
      .catch((error) => console.error("Error", error));
  };

  const onIdle = () => {
    if (mapref?.current) {
      const lat = mapref.current.center.lat();
      const lng = mapref.current.center.lng();
      const mapCenter = {
        lat: lat,
        lng: lng,
      };

      updateStreetAddress(mapCenter);
      props.handleValues("latlng", mapCenter);
    }
  };
  function locateMe() {
    props.setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(showPosition, errorFn);
    } else {
      props.setLoading(false);
      errorNotification("Geolocation is not supported by this browser.");
    }
  }

  function showPosition(position) {
    props.setLoading(false);
    var lat = position.coords.latitude;
    var lng = position.coords.longitude;
    const latLng = {
      lat,
      lng,
    };
    props.handleValues("latlng", latLng);
    updateStreetAddress(latLng);
    updateZoom(21);
  }

  function errorFn(error) {
    props.setLoading(false);
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorNotification(
          "User denied the request for Geolocation.Please Enable location from settings"
        );
        break;
      case error.POSITION_UNAVAILABLE:
        errorNotification("Location information is unavailable.");
        break;
      case error.TIMEOUT:
        errorNotification("The request to get user location timed out.");
        break;
      case error.UNKNOWN_ERROR:
        errorNotification(
          "An unknown error occurred while accessing current location."
        );
        break;
      default:
        errorNotification(
          "An unknown error occurred while accessing current location."
        );
        break;
    }
  }

  const updateStreetAddress = (mapCenter) => {
    var geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ latLng: mapCenter }, function (results, status) {
      if (status === window.google.maps.GeocoderStatus.OK) {
        if (results[0]) {
          props.handleValues("mapAddress", results[0].formatted_address);
        }
      }
    });
  };

  const updateZoom = (z) => {
    props.setState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [C.ZOOM]: z,
      },
      errors: {
        ...prevState.errors,
        [C.ZOOM]: "",
      },
    }));
  };
  const changezoom = () => {
    if (mapref?.current?.map && props.state) {
      const latlng = {
        lat: mapref?.current?.map.center.lat(),
        lng: mapref?.current?.map.center.lng(),
      };
      let l = mapref.current.map.getCenter();
      let no = {
        lat: l.lat(),
        lng: l.lng(),
      };

      let z = mapref.current.map.zoom;
      updateZoom(z);
    }
  };
  const handleOnLoad = (map) => (mapref.current = map);

  const onUnmount = useCallback(() => {
    console.log("unmount");
    mapref.current = null;
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: "script-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    language: "en",
    region: "us",
    libraries: libraries,
  });

  const mapAddressError =
    props?.state?.errors?.mapAddress || props?.state?.errors?.manualAddress;
  const mapHeight =
    typeof props?.height === "string" && props.height.includes("px")
      ? props.height
      : props?.height || "min(420px, 55vh)";

  return (
    <>
      {isLoaded ? (
        <div
          className="flex w-full flex-col gap-0"
          style={{
            minHeight: props?.height || undefined,
          }}
        >
          <div
            className={`isolate ${props?.className || "w-full"}`}
          >
            {/* Stacking: map is the next sibling; without z-index the map paints over the
                Places dropdown (suggestions are position:absolute in this block). */}
            <div className="relative z-20">
            <AppFieldWithLabelAndError
              label={props.label || t("Enter Google Map Location")}
              error={mapAddressError}
            >
              <PlacesAutocomplete
                disabled={props?.disabled}
                value={props?.state?.values?.mapAddress || ""}
                onChange={handleChange}
                onSelect={handleSelect}
                searchOptions={{
                  componentRestrictions: { country: ["ae"] },
                }}
              >
                {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading,
                }) => (
                  <div className="relative w-full">
                    <div
                      className={`
                        flex w-full items-center gap-2 rounded-xl border border-gray-300 bg-white
                        px-3 py-2.5 shadow-sm transition
                        focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20
                        ${props?.disabled ? "opacity-60 pointer-events-none" : ""}
                      `}
                    >
                      <input
                        type="text"
                        {...getInputProps({
                          placeholder:
                            props?.placeholder || t("Location"),
                          className:
                            "min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0",
                        })}
                      />
                      <button
                        type="button"
                        onClick={locateMe}
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-blue-600 transition hover:bg-gray-50"
                        aria-label={t("Use my current location")}
                      >
                        <svg
                          viewBox="0 0 22 22"
                          fill="none"
                          className="h-5 w-5"
                          aria-hidden
                        >
                          <path
                            d="M8.333 11a2.667 2.667 0 105.333 0 2.667 2.667 0 00-5.333 0z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4.333 11a6.667 6.667 0 1013.334 0 6.667 6.667 0 00-13.334 0v0zM11 1v3.333M1 11h3.333M11 21v-3.333M21 11h-3.333"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {(suggestions.length > 0 || loading) && (
                      <div
                        className="autocomplete-dropdown-container absolute left-0 right-0 z-[1000] mt-1 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                        style={props?.autocompleteDropdownStyle}
                      >
                        {loading && (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            {t("Loading...")}
                          </div>
                        )}
                        {suggestions.map((suggestion, key) => {
                          const active = suggestion.active;
                          return (
                            <div
                              key={key}
                              {...getSuggestionItemProps(suggestion, {
                                className: `cursor-pointer px-3 py-2.5 text-sm ${
                                  active ? "bg-gray-50" : "bg-white"
                                }`,
                              })}
                            >
                              <span className="text-gray-800">
                                {suggestion?.description}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </PlacesAutocomplete>
            </AppFieldWithLabelAndError>
            </div>

            <div className="relative z-0 mt-4 overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                  <MapGoogle
                    // onReady={onReady}
                    // gestureHandling="greedy"
                    // ref={mapref}
                    // onDragend={onIdle}
                    // onZoomChanged={changezoom}
                    // zoom={
                    //   props?.state?.values?.zoom
                    //     ? props?.state?.values?.zoom
                    //     : props.zoom
                    //     ? props.zoom
                    //     : 8
                    // }
                    // google={props?.google ? props?.google : null}
                    // style={{
                    //   height: "100%",
                    //   width: "95%",
                    // }}
                    // initialCenter={{
                    //   lat: props?.state?.values?.storeLocation?.coordinates?.[0]
                    //     ? props?.state?.values?.storeLocation?.coordinates[0]
                    //     : 24,
                    //   lng: props?.state?.values?.storeLocation?.coordinates?.[1]
                    //     ? props?.state?.values?.storeLocation?.coordinates[1]
                    //     : 53,
                    // }}
                    // center={{
                    //   lat: props?.state?.values?.storeLocation?.coordinates?.[0]
                    //     ? props?.state?.values?.storeLocation?.coordinates?.[0]
                    //     : 24,
                    //   lng: props?.state?.values?.storeLocation?.coordinates?.[1]
                    //     ? props?.state?.values?.storeLocation?.coordinates?.[1]
                    //     : 53,
                    // }}

                    key="google-map"
                    id="myMap"
                    ref={mapref}
                    onDragEnd={onIdle}
                    onZoomChanged={changezoom}
                    mapContainerClassName="block h-full w-full"
                    zoom={
                      props?.state?.values?.zoom
                        ? props?.state?.values?.zoom
                        : props.zoom
                        ? props.zoom
                        : 8
                    }
                    version="weekly"
                    mapContainerStyle={{
                      height: mapHeight,
                      width: "100%",
                    }}
                    options={{
                      gestureHandling: "greedy",
                      disableDefaultUI: true,
                      keyboardShortcuts: false,
                    }}
                    center={{
                      lat: props?.state?.values?.storeLocation?.coordinates?.[0]
                        ? props?.state?.values?.storeLocation?.coordinates?.[0]
                        : 24,
                      lng: props?.state?.values?.storeLocation?.coordinates?.[1]
                        ? props?.state?.values?.storeLocation?.coordinates?.[1]
                        : 53,
                    }}
                    onLoad={handleOnLoad}
                    onUnmount={onUnmount}
                  >
                    <Marker
                      position={{
                        lat: props?.state?.values?.storeLocation
                          ?.coordinates?.[0]
                          ? props?.state?.values?.storeLocation?.coordinates[0]
                          : 24,
                        lng: props?.state?.values?.storeLocation
                          ?.coordinates?.[1]
                          ? props?.state?.values?.storeLocation?.coordinates[1]
                          : 53,
                      }}
                      icon={{
                        url: TARGET_MARKER_ICON,
                      }}
                    />
                  </MapGoogle>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
          {t("Loading map...")}
        </div>
      )}
    </>
  );
};

MyMap.propTypes = {
  google: PropTypes.number,
  key: PropTypes.string,
  handleValues: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,
  state: PropTypes.object,
  store: PropTypes.object,
  label: PropTypes.string,
  placeholder: PropTypes.string,
};

export default MyMap;
